/**
 * Table component for displaying scheduled appointments
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar } from 'lucide-react';
import { ScheduledAppointment } from '@/services/features/ScheduledAppointmentsService';
import { toZonedTime, format } from 'date-fns-tz';

interface ScheduledAppointmentsTableProps {
  appointments: ScheduledAppointment[];
  isLoading: boolean;
  type: 'virtual'| 'in-person';
  emptyMessage?: string;
}

export const ScheduledAppointmentsTable: React.FC<ScheduledAppointmentsTableProps> = ({
  appointments,
  isLoading,
  type,
  emptyMessage = 'No appointments found',
}) => {
  const navigate = useNavigate();

  const handlePatientClick = (enrollmentPatientId: number | null) => {
    if (enrollmentPatientId) {
      navigate(`/es-dashboard?from=global-search&patientId=${enrollmentPatientId}`);
    }
  };

  const formatDateTime = (dateTimeString: string, timezone: string) => {
    try {
      const utcDate = new Date(dateTimeString);
      const zonedDate = toZonedTime(utcDate, timezone);
      
      const formattedDate = format(zonedDate, 'MMM dd, yyyy', { timeZone: timezone });
      const formattedTime = format(zonedDate, 'hh:mm a', { timeZone: timezone });
      
      return { date: formattedDate, time: formattedTime };
    } catch {
      return { date: '-', time: '-' };
    }
  };

  if (isLoading) {
    return (
      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Patient Name</TableHead>
                <TableHead>Appointment Date</TableHead>
                <TableHead>Start Time</TableHead>
                <TableHead>End Time</TableHead>
                <TableHead>Clinic ID</TableHead>
                <TableHead>User ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  if (appointments.length === 0) {
    return (
      <Card className="border border-border">
        <CardContent className="p-12 text-center">
          <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-lg">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id={`table-scheduled-appointments-${type}-container`} className="border border-border">
      <CardContent className="p-0">
        <Table id={`table-scheduled-appointments-${type}`}>
          <TableHeader>
            <TableRow id={`table-scheduled-appointments-${type}-header-row`} className="bg-muted/50 hover:bg-muted/50">
              <TableHead id="header-cell-patient-name">
                <div className="flex items-center gap-2">
                  Patient Name
                </div>
              </TableHead>
              <TableHead id="header-cell-appointment-date">
                <div className="flex items-center gap-2">
                  Appointment Date
                </div>
              </TableHead>
              <TableHead id="header-cell-appointment-time">
                <div className="flex items-center gap-2">
                  Appointment Time
                </div>
              </TableHead>
              <TableHead id="header-cell-clinic-name">
                <div className="flex items-center gap-2">
                  Clinic Name
                </div>
              </TableHead>
              <TableHead id={`header-cell-${type === 'virtual' ? 'user' : 'facility'}-name`}>{type == 'virtual' ? 'User' : 'Facility'} Name</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id={`table-scheduled-appointments-${type}-body`}>
            {appointments.map((appointment) => {
              const timezone = appointment.clinic_timezone || 'UTC';
              const startDateTime = formatDateTime(appointment.appointment_date_start_time, timezone);
              const endDateTime = formatDateTime(appointment.appointment_date_end_time, timezone);

              return (
                <TableRow 
                  key={appointment.appointment_id}
                  id={`row-appointment-${appointment.appointment_id}`}
                  className="hover:bg-accent/50 transition-colors"
                >
                  <TableCell id={`cell-appointment-${appointment.appointment_id}-patient`} className="font-medium text-foreground">
                    {appointment.enrollment_patient_id ? (
                      <button
                        id={`btn-appointment-${appointment.appointment_id}-patient-link`}
                        onClick={() => handlePatientClick(appointment.enrollment_patient_id)}
                        className="text-primary hover:text-primary/80 hover:underline cursor-pointer transition-colors text-left"
                      >
                        {appointment.patient_name || '-'}
                      </button>
                    ) : (
                      appointment.patient_name || '-'
                    )}
                  </TableCell>
                  <TableCell id={`cell-appointment-${appointment.appointment_id}-date`} className="text-foreground">
                    {startDateTime.date}
                  </TableCell>
                  <TableCell id={`cell-appointment-${appointment.appointment_id}-time`} className="text-foreground">
                    {startDateTime.time} - {endDateTime.time} ({appointment.clinic_timezone})
                  </TableCell>
                  <TableCell id={`cell-appointment-${appointment.appointment_id}-clinic`} className="text-foreground">
                    {appointment.clinic_name || '-'}
                  </TableCell>
                  <TableCell id={`cell-appointment-${appointment.appointment_id}-${type === 'virtual' ? 'user' : 'facility'}`} className="text-foreground">
                    {type == 'virtual' ? (appointment.user_name) : (appointment.facility_name)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
